/* eslint-disable react/no-unescaped-entities, no-useless-return */
import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  CustomInput,
  FormGroup,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Modal,
  ModalBody,
  ModalHeader,
} from 'reactstrap';
import * as Yup from 'yup';
import { useLocation } from 'react-router-dom';
import { Flip, toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import QRCodeLib from 'react-qr-code';
import { Form } from '@unform/web';
import PixIcon from '@mui/icons-material/Pix';
import { Scope } from '@unform/core';
import { parse, isDate, isAfter, differenceInYears, isValid } from 'date-fns';
import {
  normalizeState,
  normalizePostCode,
  splitBRPhone,
  normalizePan,
  normalizeEmailFor3DS,
  isValidLuhn,
  parseExpiry,
  load3DSScriptV2,
  waitForTDS,
  handle3DSAuthenticationV2,
  evaluate3DSStatus,
} from '~/util/3dsHelper';

import { sendThreeDSDebug } from '~/util/threeDSDebugService';
import { resolve3DSFailure } from '~/util/threeDSErrorMapper';
import {
  PAYMENT_TYPE_APPLE_PAY,
  PAYMENT_TYPE_GOOGLE_PAY,
  isApplePayBrowser,
  isGooglePayReady,
  isWalletCanceled,
  requestApplePayToken,
  requestGooglePayToken,
} from '~/services/wallets';
import { ApplePayButton, GooglePayButton } from '~/components/WalletButtons';
import WalletPicker, {
  PAY_MODE_APPLE_PAY,
  PAY_MODE_CARD,
  PAY_MODE_GOOGLE_PAY,
  WalletNote,
} from '~/components/WalletPicker';
import PaymentAlternatives from '~/components/PaymentAlternatives';

import { copyStringToClipboard } from '~/util/copyToClipboard';
import { formatPrice, formatWhatsappPhoneNumber } from '~/util/format';

import SchemaValidator, {
  cpfSchema,
  emailSchema,
  holderSchema,
} from '~/validators/form/new_pix_link';
import walletCheckoutSchema from '~/validators/form/wallet_checkout';

import history from '~/services/history';
import Loading from '~/components/Loading';
import {
  finalize,
  loadCart,
  setPaymentData,
} from '~/store/modules/shareable_cart/actions';
import InputSelect from '~/components/Inputs/InputSelect';
import InputMask from '~/components/Inputs/InputMask';
import CardFlag from '~/util/CardFlag';
import { installmentsLimited } from '~/util/installmentsList';
import { maskBuilder } from '~/util/maskTelephone';
import { stateList } from '~/util/stateList';
import api from '~/services/api';
import { getCitiesByUf } from '~/util/ibge';
import Input from '~/components/Inputs/Input';
import isDarkColor from '~/util/isDarkColor';
import TextAreaWithCounter from '~/components/Inputs/TextAreaWithCounter';
import { useLocalStorage } from '~/hooks/useLocalStorage';
import { getSubscriberIdFromToken } from '~/util/checkout';
import { formatCheckoutError } from './checkoutErrorMapper';

/** VISA: começa com 4 e tem 13/16/19 dígitos */
const isVisaPan = pan => /^4\d{12}(\d{3})?(\d{3})?$/.test(pan);
const isVisaBrand = b => /^visa$/i.test(String(b || '').trim());

/**
 * MASTERCARD:
 * - 51–55 (2 primeiros dígitos)
 * - 2221–2720 (4 primeiros dígitos)
 * - normalmente 16 dígitos
 */
const isMastercardPan = pan => {
  const p = String(pan || '');
  if (!/^\d{16}$/.test(p)) return false;

  const prefix2 = Number(p.slice(0, 2));
  const prefix4 = Number(p.slice(0, 4));

  return (
    (prefix2 >= 51 && prefix2 <= 55) || (prefix4 >= 2221 && prefix4 <= 2720)
  );
};

const isMastercardBrand = b =>
  /^(mastercard|master\s?card|mc)$/i.test(String(b || '').trim());

const get3DSBrandKey = (brandInput, panRaw) => {
  const pan = normalizePan(panRaw);

  if (isVisaBrand(brandInput) || isVisaPan(pan)) return 'visa';
  if (isMastercardBrand(brandInput) || isMastercardPan(pan))
    return 'mastercard';

  return null;
};

function toCents3DS(value) {
  const str = String(value ?? '').trim();
  if (!str) return 0;

  const normalized = str
    .replace(/\./g, '') // Remove milhares
    .replace(',', '.'); // Vírgula decimal -> Ponto

  const n = Number(normalized);
  if (!Number.isFinite(n)) return 0;

  return Math.round(n * 100);
}

function build3DSItemsFromCart(cart, purchaseAmountCents) {
  const products = Array.isArray(cart?.products) ? cart.products : [];

  const items = products
    .map((p, idx) => {
      const qtyRaw = p?.pivot?.prod_amount;
      const quantity = Math.max(1, Number(qtyRaw || 1) || 1);

      const unitCents = toCents3DS(p?.pivot?.value);
      if (!unitCents || unitCents <= 0) return null;

      const code = String(p?.code || p?.id || `ITEM-${idx + 1}`);

      const variationParts = [
        p?.pivot?.size ? `Tam ${p.pivot.size}` : null,
        p?.pivot?.color ? `Cor ${p.pivot.color}` : null,
      ].filter(Boolean);

      const description = String(
        [p?.name || `Item ${idx + 1}`, ...variationParts].join(' - ')
      ).slice(0, 255);

      return {
        amount: unitCents, // Valor unitário em centavos
        quantity,
        description,
        code,
      };
    })
    .filter(Boolean);

  if (items.length) return items;

  // Fallback: 1 item agregado (evita products: [])
  const fallbackAmount = Number(purchaseAmountCents || 0);

  return [
    {
      amount: fallbackAmount > 0 ? fallbackAmount : 1,
      quantity: 1,
      description: `Produtos - ${cart?.store?.name || 'Viddy'}`,
      code: String(
        cart?.order_number ? `CART-${cart.order_number}` : 'ITEM-001'
      ),
    },
  ];
}

export default function LinkPix() {
  const formRef = useRef(null);
  const toastId = useRef(null);
  const useQuery = () => new URLSearchParams(useLocation().search);
  const dispatch = useDispatch();

  const [errorCheckout, setErrorCheckout] = useState(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const client = useSelector(({ shareable_cart }) => shareable_cart?.client);

  const storeOnlinePaymentLimit = Number(
    useSelector(
      ({ shareable_cart }) =>
        shareable_cart.cart?.store?.online_payments_value_limit
    )
  );

  const amount = useSelector(
    ({ shareable_cart }) => shareable_cart.cart?.amount
  );

  const [lastPixFinalizeCallCheckout, setLastPixFinalizeCallCheckout] =
    useLocalStorage('last_pix_finalize_call_checkout', 0);
  const [telephoneV, setTelephone] = useState('');

  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [cityList, setCityList] = useState([]);
  const [searchedCEP, setSearchedCEP] = useState('');
  const [disabled, setDisabled] = useState(false);

  const [noNumber, setNoNumber] = useState(false);

  const [brand, setBrand] = useState(null);
  const [paymentType, setPaymentType] = useState(null);

  const loading = useSelector(({ shareable_cart }) => shareable_cart.loading);

  const paymentProcess = useSelector(
    ({ shareable_cart }) => shareable_cart?.payment_process
  );

  const paymentGateway = useSelector(
    ({ shareable_cart }) => shareable_cart.cart?.store?.payment_gateway
  );

  const hasPixKey = useSelector(
    ({ shareable_cart }) => shareable_cart.cart?.store?.has_pix_key
  );

  const cartQrCode = useSelector(
    ({ shareable_cart }) => shareable_cart?.cart?.payment_process?.qrcode
  );

  const [loadingPage, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const token = useQuery().get('token');
  const id = useQuery().get('id');

  const cart = useSelector(({ shareable_cart }) => shareable_cart?.cart);

  const subscriberId = getSubscriberIdFromToken(token);
  const subscriberViddyComercial = '504c2f33-f3af-411c-b6f4-c9c483f8b745';
  const viddyTax = 1.99;

  const themeIsWhite = cart?.store?.customize?.theme_color === '#ffffff';

  const clientData = useSelector(({ shareable_cart }) => shareable_cart.client);

  const cartAddress = useSelector(
    ({ shareable_cart }) => shareable_cart?.cart?.delivery_address
  );

  const [is3DSModalOpen, setIs3DSModalOpen] = useState(false);
  const [is3DSLoading, setIs3DSLoading] = useState(false);
  const [threeDSError, setThreeDSError] = useState(null);
  const [googlePayReady, setGooglePayReady] = useState(false);
  const applePayReady = useMemo(() => isApplePayBrowser(), []);
  const [payMode, setPayMode] = useState(PAY_MODE_CARD);
  const [is3DSProcessing, setIs3DSProcessing] = useState(false);
  const [is3DSSettling, setIs3DSSettling] = useState(false);
  const threeDSOpenRef = useRef(false);
  const threeDSErrorRef = useRef(null);
  const threeDSWatchdogRef = useRef(null);
  const threeDSChallengeSeenRef = useRef(false);
  const threeDSSettleTimerRef = useRef(null);
  const threeDSSettleRejectRef = useRef(null);
  const threeDSModalOpenedResolverRef = useRef(null);
  const threeDSUserClosedRef = useRef(false);
  const threeDSObserverRef = useRef(null);
  const threeDSRunIdRef = useRef(0);

  const build3DSContext = useCallback(
    () => ({
      storeId: cart?.store?.id,
      storeName: cart?.store?.fantasy_name,
      gateway: paymentGateway,
    }),
    [cart?.store?.id, cart?.store?.fantasy_name, paymentGateway]
  );

  const closeThreeDSModal = useCallback(() => {
    threeDSRunIdRef.current += 1;
    threeDSUserClosedRef.current = true;
    setIs3DSProcessing(false);
    setIs3DSSettling(false);
    threeDSOpenRef.current = false;
    threeDSChallengeSeenRef.current = false;
    threeDSSettleRejectRef.current = null;
    if (threeDSSettleTimerRef.current) {
      clearTimeout(threeDSSettleTimerRef.current);
      threeDSSettleTimerRef.current = null;
    }
    threeDSErrorRef.current = null;
    if (threeDSWatchdogRef.current) {
      clearTimeout(threeDSWatchdogRef.current);
      threeDSWatchdogRef.current = null;
    }
    setIs3DSModalOpen(false);
    setIs3DSLoading(false);
    setThreeDSError(null);
    setDisabled(false);
    if (threeDSObserverRef.current) {
      threeDSObserverRef.current.disconnect();
      threeDSObserverRef.current = null;
    }
    if (threeDSModalOpenedResolverRef.current) {
      threeDSModalOpenedResolverRef.current();
      threeDSModalOpenedResolverRef.current = null;
    }
    setTimeout(() => {
      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
    }, 300);
  }, []);

  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [expirationDate, setExpirationDate] = useState('');

  const validateBirthDate = value => {
    if (!value || value.length !== 10) {
      formRef.current.setFieldError(
        'birth_date',
        'A data deve estar no formato dd/mm/aaaa.'
      );
      return false;
    }

    const [dayStr, monthStr, yearStr] = value.split('/');
    const day = Number(dayStr);
    const month = Number(monthStr);
    const year = Number(yearStr);

    if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) {
      formRef.current.setFieldError('birth_date', 'Data inválida');
      return false;
    }

    if (day < 1 || day > 31) {
      formRef.current.setFieldError('birth_date', 'Dia inválido');
      return false;
    }

    if (month < 1 || month > 12) {
      formRef.current.setFieldError('birth_date', 'Mês inválido');
      return false;
    }

    const currentYear = new Date().getFullYear();
    if (year > currentYear) {
      formRef.current.setFieldError('birth_date', 'Ano não pode ser no futuro');
      return false;
    }

    const parsedDate = parse(value, 'dd/MM/yyyy', new Date());

    if (!isValid(parsedDate)) {
      formRef.current.setFieldError('birth_date', 'Data inválida');
      return false;
    }

    if (isAfter(parsedDate, new Date())) {
      formRef.current.setFieldError(
        'birth_date',
        'Data de nascimento não pode ser no futuro'
      );
      return false;
    }

    const idade = differenceInYears(new Date(), parsedDate);
    if (idade < 10) {
      formRef.current.setFieldError(
        'birth_date',
        'É necessário ter mais de 10 anos'
      );
      return false;
    }

    formRef.current.setFieldError('birth_date', null);
    return true;
  };

  const safeScrollToField = rawPath => {
    const path = String(rawPath || '').trim();
    if (!path) return;

    const field = path.includes('.') ? path.split('.').pop() : path;

    try {
      const dom =
        document.querySelector(`[name="${path}"]`) ||
        document.querySelector(`[name="${field}"]`) ||
        document.getElementById(field) ||
        document.getElementById(path);

      if (dom && typeof dom.scrollIntoView === 'function') {
        dom.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    if (cart?.store?.customize?.theme_color) {
      setIsDarkTheme(isDarkColor(cart?.store?.customize?.theme_color));
      document.documentElement.style.setProperty(
        '--primary',
        cart?.store?.customize?.theme_color
      );
    }
  }, [cart?.store?.customize?.theme_color]);

  useEffect(() => {
    const isProduction = process.env.REACT_APP_3DS_ENV === 'production';

    load3DSScriptV2({
      isProduction,
      scriptId: 'stone-3ds-script',
      debugKey: '__stone3ds',
    }).catch(err => {
      console.error('[3DS] Falha ao carregar script:', err?.message || err);
    });

    return () => {
      const existing = document.getElementById('stone-3ds-script');
      if (existing) existing.remove();
    };
  }, []);

  useEffect(() => {
    if (paymentType !== 'Pix') return null;

    const refreshInterval = setInterval(() => {
      dispatch(loadCart(id, token));
    }, 10000);

    return () => clearInterval(refreshInterval);
  }, [paymentType, id, token, dispatch]);

  function toCents(value) {
    if (typeof value === 'number') {
      // número em reais (ex.: 1439.98)
      return Math.round((value + Number.EPSILON) * 100);
    }
    if (typeof value !== 'string')
      throw new Error('Valor inválido para amount');

    // mantém apenas dígitos, vírgula, ponto e sinal
    let s = String(value)
      .trim()
      .replace(/[^\d.,-]/g, '');

    // só dígitos? já está em centavos (ex.: "143998")
    if (/^-?\d+$/.test(s)) return Number(s);

    const hasComma = s.includes(',');
    const hasDot = s.includes('.');

    if (hasComma && hasDot) {
      // quando tem os dois, o separador decimal é o ÚLTIMO que aparece
      const lastComma = s.lastIndexOf(',');
      const lastDot = s.lastIndexOf('.');
      const decSep = lastComma > lastDot ? ',' : '.';
      const thouSep = decSep === ',' ? '.' : ',';
      s = s.replace(new RegExp(`\\${thouSep}`, 'g'), ''); // remove milhar
      s = s.replace(decSep, '.'); // normaliza decimal
    } else if (hasComma) {
      s = s.replace(/\./g, ''); // pontos de milhar
      s = s.replace(/,/g, '.'); // vírgula como decimal
    } else {
      s = s.replace(/,/g, ''); // vírgulas de milhar
    }

    const n = Number(s);
    if (!Number.isFinite(n)) throw new Error('Valor inválido para amount');

    return Math.round((n + Number.EPSILON) * 100);
  }

  function cartTotal(cartSale) {
    const amountSale = Number(cartSale?.amount || 0);
    const freightSale = Number(cartSale?.freight_user || 0);
    const giftbackSale = Number(cartSale?.giftback?.amount || 0);
    return amountSale + freightSale - giftbackSale;
  }

  const validateData = useCallback(
    async (rawData, options = {}) => {
      let data = {
        ...rawData,
        email: rawData?.email?.trim(),
        installments: rawData?.installments?.value,
        address: {
          ...rawData.address,
        },
        expiration_date: `${expirationDate.split('/')[0]}/20${
          expirationDate.split('/')[1]
        }`,
      };

      if (data.client_is_payer) {
        data = {
          ...data,
          email: data?.email?.trim(),
          address: {
            additional_information: cartAddress?.additional_information || '',
            cep: cartAddress?.cep || '',
            complement: cartAddress?.complement || '',
            neighborhood: cartAddress?.neighborhood || '',
            number: cartAddress?.number || '',
            street_name: cartAddress?.street_name || '',
            city: cartAddress?.city,
            state: cartAddress?.state,
          },
        };
      }

      const month = data?.expiration_date?.split('/')[0];
      const year = data?.expiration_date?.split('/')[1];

      const verifyData = {
        ...data,
        expiration_date: new Date(
          year,
          month > 0 && month <= 12 ? month : 'null'
        ),
      };

      const maskTelephoneLength = maskBuilder(telephoneV).length - 1;

      formRef.current.setErrors({});

      const validationSchema = options.wallet
        ? walletCheckoutSchema(maskTelephoneLength)
        : SchemaValidator(maskTelephoneLength);

      await validationSchema.validate(verifyData, {
        abortEarly: false,
      });

      if (!options.wallet && !data.brand) {
        formRef.current?.setFieldError(
          'card_number',
          'Não foi possível identificar a bandeira desse cartão'
        );

        safeScrollToField('card_number');

        try {
          const el = document.querySelector('[name="card_number"]');
          el?.focus?.();
        } catch (e) {
          // ignore
        }

        return false;
      }

      return data;
    },
    [cartAddress, expirationDate, telephoneV]
  );

  const cartAvailable = useMemo(() => {
    if (
      (storeOnlinePaymentLimit === 0 || amount <= storeOnlinePaymentLimit) &&
      (cart?.store.has_pix_key ||
        cart?.store.has_pagarme_key ||
        cart?.store.has_cielo_merchant_keys ||
        cart?.store.has_mercadopago_key ||
        cart?.store.has_justa_key ||
        cart?.store.has_pagseguro_key) &&
      (!cart?.display_payment || !cart?.display_payment.includes('hide-card'))
    ) {
      return true;
    }
    return false;
  }, [storeOnlinePaymentLimit, amount, cart]);

  const pixPaymentAvailable = useMemo(() => {
    if (
      (cart?.store.has_pix_key ||
        cart?.store.has_pagarme_key ||
        cart?.store.has_cielo_merchant_keys ||
        cart?.store.has_mercadopago_key ||
        cart?.store.has_justa_key ||
        cart?.store.has_pagseguro_key) &&
      (!cart?.display_payment || !cart?.display_payment.includes('hide-pix'))
    ) {
      return true;
    }
    return false;
  }, [cart]);

  // Carteiras digitais (Google Pay / Apple Pay via Pagar.me): flag efetiva da
  // loja (backend) + gateway Pagar.me + navegador com suporte + mesmas
  // restrições do cartão (limite de valor / hide-card).
  const walletGatewayAvailable =
    paymentGateway === 'pagarme' || paymentGateway === 'pagarme_hub';

  const googlePayAvailable = useMemo(
    () =>
      cartAvailable &&
      walletGatewayAvailable &&
      !!cart?.store?.google_pay_available &&
      !!cart?.store?.google_pay_gateway_merchant_id &&
      googlePayReady,
    [cart, cartAvailable, walletGatewayAvailable, googlePayReady]
  );

  const applePayAvailable = useMemo(
    () =>
      cartAvailable &&
      walletGatewayAvailable &&
      !!cart?.store?.apple_pay_available &&
      applePayReady,
    [cart, cartAvailable, walletGatewayAvailable, applePayReady]
  );

  const isWalletMode = payMode !== PAY_MODE_CARD;
  const walletLabel =
    payMode === PAY_MODE_GOOGLE_PAY ? 'Google Pay' : 'Apple Pay';
  const storeGooglePayAvailable = !!cart?.store?.google_pay_available;

  // Se a carteira selecionada deixar de estar disponível, volta para cartão.
  useEffect(() => {
    if (
      (payMode === PAY_MODE_GOOGLE_PAY && !googlePayAvailable) ||
      (payMode === PAY_MODE_APPLE_PAY && !applePayAvailable)
    ) {
      setPayMode(PAY_MODE_CARD);
    }
  }, [payMode, googlePayAvailable, applePayAvailable]);

  useEffect(() => {
    let cancelled = false;

    if (!storeGooglePayAvailable || !walletGatewayAvailable) {
      setGooglePayReady(false);
      return undefined;
    }

    isGooglePayReady().then(ready => {
      if (!cancelled) setGooglePayReady(ready);
    });

    return () => {
      cancelled = true;
    };
  }, [storeGooglePayAvailable, walletGatewayAvailable]);

  const consoleLogsRef = useRef([]);

  const interceptConsole = methodName => {
    const originalMethod = console[methodName];
    console[methodName] = (...args) => {
      const message = `[${methodName.toUpperCase()}] ${args.join(' ')}`;
      consoleLogsRef.current = [...consoleLogsRef.current, message];
      originalMethod.apply(console, args);
    };
  };

  if (process.env.NODE_ENV !== 'production') {
    ['log', 'warn', 'error', 'info'].forEach(interceptConsole);
  }

  const buildPreviewPayload = data => ({
    name: data.name,
    telephone: data.telephone,
    cpf: data.cpf,
    email: data.email,
    address: {
      cep: data?.address?.cep || '',
      street_name: data?.address?.street_name || '',
      number: String(data?.address?.number || ''),
      neighborhood: data?.address?.neighborhood || '',
      complement: data?.address?.complement || '',
      state: data?.address?.state || '',
      city: data?.address?.city || '',
    },
  });

  const saveThreeDSStatus = useCallback(
    async ({
      cartId,
      tds_server_trans_id,
      threeds_status,
      threeds_message,
    }) => {
      try {
        await api.patch(
          `/shareable-carts/carts/${cartId}/threeds/pagarme`,
          { tds_server_trans_id, threeds_status, threeds_message },
          { headers: { 'X-Shareable-Token': token } }
        );
      } catch (e) {
        console.error('[3DS] Falha ao salvar status 3DS:', e?.message || e);
      }
    },
    [token]
  );

  const handleCheckoutError = useCallback(
    (message, code) => {
      setError(message);

      if (threeDSWatchdogRef.current) {
        clearTimeout(threeDSWatchdogRef.current);
        threeDSWatchdogRef.current = null;
      }

      if (!message || !threeDSOpenRef.current) return;

      const isGatewayCode =
        code && !['MESSAGE_ONLY', 'UNKNOWN_ERROR'].includes(code);

      const mapped = {
        title: 'Pagamento não aprovado',
        message: String(message),
        code: isGatewayCode ? `#${code}` : 'E-PAY-001',
        reference: cart?.order_number || null,
      };

      threeDSErrorRef.current = mapped;
      setIs3DSProcessing(false);
      setIs3DSLoading(false);
      setThreeDSError(mapped);
    },
    [cart]
  );

  const handleCheckoutLoading = useCallback(value => {
    setLoading(value);

    if (value) return;

    if (threeDSWatchdogRef.current) {
      clearTimeout(threeDSWatchdogRef.current);
      threeDSWatchdogRef.current = null;
    }

    if (threeDSOpenRef.current && !threeDSErrorRef.current) {
      setIs3DSProcessing(false);
      setIs3DSModalOpen(false);
      threeDSOpenRef.current = false;
    }
  }, []);

  useEffect(
    () => () => {
      if (threeDSWatchdogRef.current) {
        clearTimeout(threeDSWatchdogRef.current);
        threeDSWatchdogRef.current = null;
      }
    },
    []
  );

  /**
   * Pagamento com carteira digital: valida os dados de cobrança (sem cartão),
   * abre a sheet da wallet, recebe o token e chama o mesmo finalize do cartão.
   * Wallet não passa por 3DS (o token já é autenticado) nem pelo preview do
   * antifraude (que só decide 3DS); a análise do Antifraude Viddy continua
   * no backend, pós-autorização.
   */
  const handleWalletSubmit = useCallback(
    async walletTag => {
      const isGoogle = walletTag === 'GooglePay';
      const rawData = formRef?.current?.getData();

      const emailNormalized = String(rawData?.email || '')
        .trim()
        .toLowerCase();
      formRef.current?.setFieldValue('email', emailNormalized);

      setDisabled(true);
      setErrorCheckout(null);
      setError(null);

      try {
        const data = await validateData(
          { ...rawData, email: emailNormalized },
          { wallet: true }
        );

        if (!data) return;

        if (cart?.client_finish) {
          await api.patch(
            `/shareable-carts/clients/${
              cart?.client?.id || clientData?.id
            }/address`,
            data.address,
            {
              headers: { 'X-Shareable-Token': token },
            }
          );
        }

        const merchantName = cart?.store?.fantasy_name || cart?.store?.name;
        const totalAmount = cartTotal(cart);

        const walletToken = isGoogle
          ? await requestGooglePayToken({
              gatewayMerchantId: cart?.store?.google_pay_gateway_merchant_id,
              merchantName,
              amount: totalAmount,
            })
          : await requestApplePayToken({
              merchantName,
              amount: totalAmount,
              onValidateMerchant: async validationURL => {
                const { data: session } = await api.post(
                  `/shareable-carts/carts/${cart.id}/apple-pay/session`,
                  {
                    validation_url: validationURL,
                    domain_name: window.location.hostname,
                  },
                  { headers: { 'X-Shareable-Token': token } }
                );
                return session;
              },
            });

        dispatch(
          setPaymentData({
            ...data,
            payment_type: isGoogle
              ? PAYMENT_TYPE_GOOGLE_PAY
              : PAYMENT_TYPE_APPLE_PAY,
            wallet_token: walletToken,
          })
        );

        dispatch(
          finalize({
            page_state: {
              setLoading: handleCheckoutLoading,
              setError: handleCheckoutError,
              formatCheckoutError,
            },
          })
        );
      } catch (err) {
        if (isWalletCanceled(err)) return;

        console.error(`Erro no checkout (${walletTag}):`, err);

        if (err instanceof Yup.ValidationError) {
          const validationErrors = {};
          const firstPath = err?.inner?.[0]?.path || err?.path;

          safeScrollToField(firstPath);

          (err.inner || []).forEach(e => {
            if (e?.path) validationErrors[e.path] = e.message;
          });
          formRef.current?.setErrors(validationErrors);
          return;
        }

        const message =
          err?.response?.data?.message ||
          (isGoogle
            ? 'Não foi possível concluir com o Google Pay. Tente novamente ou use outra forma de pagamento.'
            : 'Não foi possível concluir com o Apple Pay. Tente novamente ou use outra forma de pagamento.');

        toast.error(message, { autoClose: 6000 });
        setError(message);
      } finally {
        setDisabled(false);
      }
    },
    [
      cart,
      clientData,
      dispatch,
      token,
      validateData,
      handleCheckoutError,
      handleCheckoutLoading,
    ]
  );

  const handleSubmit = useCallback(
    async _ => {
      const rawData = formRef?.current?.getData();

      const emailNormalized = String(rawData?.email || '')
        .trim()
        .toLowerCase();
      formRef.current?.setFieldValue('email', emailNormalized);

      try {
        await emailSchema.validate(emailNormalized);
        formRef.current?.setFieldError('email', null);
      } catch (err) {
        formRef.current?.setFieldError('email', err.message);
        setDisabled(false);
        return;
      }

      setDisabled(true);
      setErrorCheckout(null);

      try {
        if (!paymentType && !cartAvailable && !pixPaymentAvailable) {
          const maskTelephoneLength = maskBuilder(telephoneV).length - 1;

          const schema = Yup.object().shape({
            name: Yup.string().required('Insira seu nome'),
            birth_date: Yup.string()
              .required('Insira a data de nascimento')
              .test('is-date-valid', 'Data inválida', value => {
                const parsedDate = parse(value, 'dd/MM/yyyy', new Date());
                return isDate(parsedDate);
              })
              .test(
                'is-not-future-date',
                'Não é possível incluir uma data futura',
                value => {
                  const parsedDate = parse(value, 'dd/MM/yyyy', new Date());
                  return !isAfter(parsedDate, new Date());
                }
              ),
            email: emailSchema,
            cpf: cpfSchema,
            telephone: Yup.string()
              .required('Insira seu telefone')
              .min(maskTelephoneLength, 'Insira todos os dígitos'),
            address: Yup.object().shape({
              cep: Yup.string()
                .required('Insira o CEP corretamente')
                .min(9, 'Insira todos os dígitos do CEP'),
              street_name: Yup.string().required('Insira o nome da rua'),
              number: Yup.string()
                .max(10, 'Número deve ter no máximo 10 caracteres')
                .required('Insira o número'),
              neighborhood: Yup.string().required('Insira o nome do bairro'),
              state: Yup.string().required('Selecione o estado'),
              city: Yup.string().required('Selecione a cidade'),
              complement: Yup.string()
                .notRequired()
                .max(50, 'Máximo de 50 caracteres'),
              additional_information: Yup.string()
                .notRequired()
                .max(
                  128,
                  'As informações adicionais devem ter no máximo 128 caracteres'
                ),
            }),
          });

          await schema.validate(rawData, { abortEarly: false });

          return;
        }

        const data = await validateData(rawData);

        if (!data) {
          setDisabled(false);
          return;
        }

        const antifraudViddyEnabled = !!cart?.store?.antifraud_viddy_enabled;

        let previewAntifraudViddy = {
          required_threeds: false,
          score: null,
          reasons: [],
        };

        if (antifraudViddyEnabled) {
          try {
            const payload = buildPreviewPayload(data);
            const { data: previewResp } = await api.post(
              `/shareable-carts/carts/${cart.id}/antifraud/preview`,
              payload,
              { headers: { 'X-Shareable-Token': token } }
            );
            previewAntifraudViddy = previewResp || previewAntifraudViddy;
          } catch (err) {
            console.error('[Antifraud] Falha no preview:', err?.message || err);
            previewAntifraudViddy = {
              required_threeds: false,
              score: null,
              reasons: ['preview_error'],
            };

            // Fail-open: preview falhou e seguimos sem 3DS. Se a loja tem 3DS
            // habilitado, a venda pode ter escapado da autenticação — registra.
            if (cart?.store?.use_threeds) {
              await sendThreeDSDebug({
                token,
                cartId: cart.id,
                stage: 'preview',
                error: err,
                context: {
                  ...build3DSContext(),
                  brand: data?.brand,
                  cardNumberRaw: data?.card_number,
                },
                extra: {
                  reason: 'preview_fail_open',
                  note: 'Preview do antifraude falhou; required_threeds forçado para false.',
                },
              });
            }
          }
        } else {
          localStorage.setItem(
            'checkout_debug',
            JSON.stringify({
              status: 'preview-skipped',
              reason: 'antifraud_viddy_disabled',
            })
          );
        }

        if (cart?.client_finish) {
          await api.patch(
            `/shareable-carts/clients/${
              cart?.client?.id || clientData?.id
            }/address`,
            data.address,
            {
              headers: { 'X-Shareable-Token': token },
            }
          );
        }

        if ((!paymentType && cartAvailable) || paymentType === 'Card') {
          const payments = {
            cielo: 103,
            rede: 111,
            mercadopago: 109,
            pagarme: 108,
            pagarme_hub: 108,
            pagseguro: 113,
            justa: 117,
          };

          dispatch(
            setPaymentData({
              ...data,
              card_number: data.card_number.replace(/\s/g, ''),
              payment_type: payments[paymentGateway],
            })
          );
        }

        let tds_server_trans_id = null;
        let transStatus = null;
        let orderData3ds = null;
        let url3ds = null;

        try {
          const supportedBrands =
            previewAntifraudViddy?.threeds_supported_brands || [
              'visa',
              'mastercard',
            ];

          const brandKey3ds = get3DSBrandKey(data?.brand, data?.card_number);
          const threedsEligible =
            !!brandKey3ds && supportedBrands.includes(brandKey3ds);

          const storeHas3DS = !!cart?.store?.use_threeds;
          const gatewaySupports3DS = ['pagarme', 'pagarme_hub'].includes(
            paymentGateway
          );
          const isCardSelected =
            (!paymentType && cartAvailable) || paymentType === 'Card';

          const shouldRun3DS =
            isCardSelected &&
            storeHas3DS &&
            gatewaySupports3DS &&
            threedsEligible &&
            (!antifraudViddyEnabled ||
              (antifraudViddyEnabled &&
                !!previewAntifraudViddy.required_threeds));

          if (storeHas3DS && gatewaySupports3DS && !threedsEligible) {
            const brandName = String(data?.brand || 'DESCONHECIDA')
              .trim()
              .toUpperCase();

            await saveThreeDSStatus({
              cartId: cart.id,
              tds_server_trans_id: null,
              threeds_status: 'SKIPPED_UNSUPPORTED_BRAND',
              threeds_message: `3DS indisponível para a bandeira: ${brandName}`,
            });

            /*
            await sendThreeDSDebug({
              token,
              cartId: cart.id,
              stage: 'status_eval',
              error: new Error(`Bandeira não suportada para 3DS: ${brandName}`),
              context: {
                ...build3DSContext(),
                brand: data?.brand,
                cardNumberRaw: data?.card_number,
              },
              extra: { threedsEligible, supportedBrands },
            });
            */
          }

          if (shouldRun3DS) {
            let tdsToken;
            threeDSRunIdRef.current += 1;
            const runId = threeDSRunIdRef.current;
            setThreeDSError(null);
            threeDSErrorRef.current = null;
            setIs3DSSettling(false);

            try {
              url3ds = `/shareable-carts/carts/${cart.id}/threeds/pagarme`;
              const { data: tokenResponse } = await api.get(url3ds, {
                headers: { 'X-Shareable-Token': token },
              });

              tdsToken = tokenResponse?.threeds_token;

              const tokenStatus =
                tokenResponse?.status || (tdsToken ? 'OK' : 'TOKEN_ERROR');

              // Token ausente por motivo legítimo (loja sem 3DS ou cartão já
              // autenticado): NÃO é erro. Pula o challenge e segue pro finalize.
              const skip3DSChallenge =
                !tdsToken &&
                (tokenStatus === 'DISABLED' ||
                  tokenStatus === 'ALREADY_AUTHENTICATED');

              // Token ausente inesperado: provider falhou ao criar a sessão. É erro.
              if (!tdsToken && !skip3DSChallenge) {
                await saveThreeDSStatus({
                  cartId: cart.id,
                  tds_server_trans_id: null,
                  threeds_status: 'TOKEN_MISSING',
                  threeds_message: '[E-3DS-001] tdsToken ausente do endpoint',
                });
                /*
                await sendThreeDSDebug({
                  token,
                  cartId: cart.id,
                  stage: 'token',
                  error: new Error('tdsToken não recebido do endpoint'),
                  context: {
                    ...build3DSContext(),
                    brand: data?.brand,
                    cardNumberRaw: data?.card_number,
                  },
                  extra: { tokenResponse, tokenStatus },
                });
                */

                setThreeDSError(
                  resolve3DSFailure({
                    internalStatus: 'TOKEN_MISSING',
                    reference: cart?.order_number,
                  })
                );
                threeDSOpenRef.current = true;
                setIs3DSModalOpen(true);
                setIs3DSLoading(false);
                setDisabled(false);
                return;
              }

              if (skip3DSChallenge && tokenStatus === 'DISABLED') {
                await sendThreeDSDebug({
                  token,
                  cartId: cart.id,
                  stage: 'token',
                  error: new Error(
                    '3DS exigido mas endpoint de token retornou DISABLED (contradição backend/provider).'
                  ),
                  context: {
                    ...build3DSContext(),
                    brand: data?.brand,
                    cardNumberRaw: data?.card_number,
                  },
                  extra: { reason: 'required_but_disabled', tokenStatus },
                });
              }

              if (tdsToken) {
                await waitForTDS(8000, 100);

                const modalOpenedPromise = new Promise(resolve => {
                  threeDSModalOpenedResolverRef.current = resolve;

                  setTimeout(() => {
                    if (threeDSModalOpenedResolverRef.current) {
                      threeDSModalOpenedResolverRef.current = null;
                      resolve();
                    }
                  }, 3000);
                });
                threeDSOpenRef.current = true;
                setIs3DSLoading(true);
                setIs3DSModalOpen(true);
                await modalOpenedPromise;

                if (runId !== threeDSRunIdRef.current) {
                  setIs3DSLoading(false);
                  setDisabled(false);
                  return;
                }

                const tdsMethodContainerEl =
                  document.getElementById('tdsMethodContainer');
                const challengeContainerEl =
                  document.getElementById('challengeContainer');

                if (!tdsMethodContainerEl || !challengeContainerEl) {
                  throw new Error(
                    'Containers 3DS não encontrados. Verifique render3DSModal com #tdsMethodContainer e #challengeContainer.'
                  );
                }

                const pan = normalizePan(data.card_number);

                if (pan.length < 13 || pan.length > 19 || !isValidLuhn(pan)) {
                  formRef.current?.setFieldError(
                    'card_number',
                    'Número do cartão inválido'
                  );
                  toast.error('Confira o número do cartão.');
                  setDisabled(false);
                  threeDSOpenRef.current = false;
                  setIs3DSModalOpen(false);
                  return;
                }

                const cpfDigits = String(data.cpf || '').replace(/\D/g, '');
                const { exp_month, exp_year } = parseExpiry(
                  data.expiration_date
                );
                if (
                  !Number.isInteger(exp_month) ||
                  exp_month < 1 ||
                  exp_month > 12 ||
                  !Number.isInteger(exp_year) ||
                  exp_year < 2000
                ) {
                  formRef.current?.setFieldError(
                    'expiration_date',
                    'Validade inválida'
                  );
                  toast.error('Confira a validade do cartão (MM/AA).');
                  setDisabled(false);
                  threeDSOpenRef.current = false;
                  setIs3DSModalOpen(false);
                  return;
                }
                const expMonthPadded = String(exp_month).padStart(2, '0');
                const { area_code, number: phone_number } = splitBRPhone(
                  data.telephone
                );

                const truncate = (value, max) =>
                  String(value || '')
                    .trim()
                    .slice(0, max);

                const sanitizeAddressPart = v =>
                  String(v || '')
                    .replace(/,/g, ' ') // remove vírgulas (regra crucial)
                    .replace(/\s+/g, ' ')
                    .trim();

                const splitCommaExtra = v => {
                  const raw = String(v || '').trim();
                  const parts = raw
                    .split(',')
                    .map(s => s.trim())
                    .filter(Boolean);
                  if (parts.length <= 1) return { main: raw, extra: '' };

                  return {
                    main: parts[0], // Somente a “rua” limpa
                    extra: parts.slice(1).join(', '), // O restante vai para line_2
                  };
                };

                const buildStoneAddressLines = ({
                  number,
                  street,
                  district,
                  complement,
                  additionalInformation,
                }) => {
                  const safeNumber = sanitizeAddressPart(number) || 'S/N'; // garante 3 partes
                  const { main: streetMain, extra: streetExtra } =
                    splitCommaExtra(street);

                  const safeStreet = sanitizeAddressPart(streetMain);
                  const safeDistrict = sanitizeAddressPart(district);

                  // line_1: SEMPRE exatamente "number, street, district"
                  const line_1 =
                    `${safeNumber}, ${safeStreet}, ${safeDistrict}`.slice(
                      0,
                      200
                    );

                  // line_2: complemento + “sobras” vindas da rua + info adicional
                  const extras = [
                    sanitizeAddressPart(complement),
                    sanitizeAddressPart(additionalInformation),
                    sanitizeAddressPart(streetExtra),
                  ].filter(Boolean);

                  const line_2 = (
                    extras.join(' - ') || 'Sem complemento'
                  ).slice(0, 50);

                  return { line_1, line_2 };
                };

                const { line_1: billingLine1, line_2: billingLine2 } =
                  buildStoneAddressLines({
                    number: data.address.number,
                    street: data.address.street_name,
                    district: data.address.neighborhood,
                    complement: data.address.complement,
                    additionalInformation: data.address.additional_information,
                  });

                const billingAddress = {
                  country: 'BR',
                  state: normalizeState(String(data.address.state || '')),
                  city: String(data.address.city || ''),
                  zip_code: normalizePostCode(String(data.address.cep || '')),
                  line_1: billingLine1,
                  line_2: billingLine2,
                };

                const { line_1: shippingLine1, line_2: shippingLine2 } =
                  buildStoneAddressLines({
                    number: cartAddress?.number || data.address.number,
                    street:
                      cartAddress?.street_name || data.address.street_name,
                    district:
                      cartAddress?.neighborhood || data.address.neighborhood,
                    complement:
                      cartAddress?.complement || data.address.complement,
                    additionalInformation: data.address.additional_information,
                  });

                const shippingAddress = {
                  country: 'BR',
                  state: normalizeState(
                    String(cartAddress?.state || data.address.state || '')
                  ),
                  city: String(cartAddress?.city || data.address.city || ''),
                  zip_code: normalizePostCode(
                    String(cartAddress?.cep || data.address.cep || '')
                  ),
                  line_1: shippingLine1,
                  line_2: shippingLine2,
                };

                const purchaseAmountCents = toCents(cartTotal(cart));

                orderData3ds = {
                  customer: {
                    name: truncate(String(data.name || ''), 64),
                    email: normalizeEmailFor3DS(data.email),
                    document: cpfDigits,
                    phones: {
                      mobile_phone: {
                        country_code: '55',
                        area_code: String(area_code || ''),
                        number: String(phone_number || ''),
                      },
                    },
                  },
                  payments: [
                    {
                      payment_method: 'credit_card',
                      amount: purchaseAmountCents,
                      credit_card: {
                        card: {
                          number: pan,
                          holder_name: truncate(String(data.holder || ''), 26),
                          exp_month: expMonthPadded,
                          exp_year,
                          billing_address: billingAddress,
                        },
                      },
                    },
                  ],
                  shipping: {
                    recipient_name: truncate(
                      String(data.name || cart?.client?.name || ''),
                      64
                    ),
                    address: shippingAddress,
                  },
                  items: build3DSItemsFromCart(cart, purchaseAmountCents),
                  requestor_url: window.location.origin,
                };

                setIs3DSLoading(true);

                challengeContainerEl.innerHTML = '';
                tdsMethodContainerEl.innerHTML = '';
                threeDSUserClosedRef.current = false;

                if (threeDSObserverRef.current) {
                  threeDSObserverRef.current.disconnect();
                  threeDSObserverRef.current = null;
                }

                threeDSChallengeSeenRef.current = false;

                threeDSObserverRef.current = new MutationObserver(() => {
                  const iframe = challengeContainerEl.querySelector('iframe');

                  if (iframe) {
                    threeDSChallengeSeenRef.current = true;
                    setIs3DSLoading(false);
                    setIs3DSSettling(false);
                    if (threeDSSettleTimerRef.current) {
                      clearTimeout(threeDSSettleTimerRef.current);
                      threeDSSettleTimerRef.current = null;
                    }
                    return;
                  }

                  if (
                    threeDSChallengeSeenRef.current &&
                    !threeDSSettleTimerRef.current
                  ) {
                    setIs3DSSettling(true);
                    threeDSSettleTimerRef.current = setTimeout(() => {
                      threeDSSettleTimerRef.current = null;
                      if (threeDSSettleRejectRef.current) {
                        threeDSSettleRejectRef.current(
                          new Error(
                            'Timeout: desafio 3DS concluído, mas o SDK não retornou resultado em 60s.'
                          )
                        );
                      }
                    }, 60000);
                  }
                });

                threeDSObserverRef.current.observe(challengeContainerEl, {
                  childList: true,
                  subtree: true,
                });

                if (runId !== threeDSRunIdRef.current) {
                  setDisabled(false);
                  return;
                }

                let tdsTimeoutId = null;
                const tdsTimeoutPromise = new Promise((ignore, rejectTds) => {
                  tdsTimeoutId = setTimeout(() => {
                    rejectTds(
                      new Error(
                        'Timeout: o SDK 3DS não retornou resultado em 300s.'
                      )
                    );
                  }, 300000);
                });

                const tdsSettlePromise = new Promise((ignore, rejectSettle) => {
                  threeDSSettleRejectRef.current = rejectSettle;
                });

                let tdsResult;

                try {
                  tdsResult = await Promise.race([
                    handle3DSAuthenticationV2({
                      tdsToken,
                      orderData: orderData3ds,
                      tdsMethodContainerEl,
                      challengeContainerEl,
                      challengeWindowSize: '03',
                      useDefaultChallengeIframeStyle: true,
                    }),
                    tdsTimeoutPromise,
                    tdsSettlePromise,
                  ]);
                } finally {
                  if (tdsTimeoutId) {
                    clearTimeout(tdsTimeoutId);
                  }
                  threeDSSettleRejectRef.current = null;
                  if (threeDSSettleTimerRef.current) {
                    clearTimeout(threeDSSettleTimerRef.current);
                    threeDSSettleTimerRef.current = null;
                  }
                }

                if (runId !== threeDSRunIdRef.current) {
                  setDisabled(false);
                  return;
                }

                transStatus = tdsResult?.trans_status || 'UNKNOWN';
                tds_server_trans_id = tdsResult?.tds_server_trans_id || null;
                const challengeCanceled = !!tdsResult?.challenge_canceled;

                const { action } = evaluate3DSStatus(transStatus);
                // console.log(`[3DS] Status avaliado: ${action} - ${message}`);

                if (threeDSUserClosedRef.current) {
                  await saveThreeDSStatus({
                    cartId: cart.id,
                    tds_server_trans_id: tds_server_trans_id || null,
                    threeds_status: 'C',
                    threeds_message: 'Challenge cancelado pelo usuário.',
                  });
                  toast.error(
                    'Você cancelou a autenticação de segurança. Tente novamente.'
                  );
                  setDisabled(false);
                  return;
                }

                switch (action) {
                  case 'proceed':
                  case 'proceed_risk':
                    await saveThreeDSStatus({
                      cartId: cart.id,
                      tds_server_trans_id,
                      threeds_status: transStatus,
                      threeds_message:
                        action === 'proceed'
                          ? 'Desafio requerido e concluído com sucesso!'
                          : '3DS indisponível/informacional (prosseguindo por risco).',
                    });

                    if (threeDSObserverRef.current) {
                      threeDSObserverRef.current.disconnect();
                      threeDSObserverRef.current = null;
                    }
                    setIs3DSLoading(false);
                    setIs3DSSettling(false);
                    setIs3DSProcessing(true);

                    if (threeDSWatchdogRef.current) {
                      clearTimeout(threeDSWatchdogRef.current);
                    }
                    threeDSWatchdogRef.current = setTimeout(() => {
                      threeDSWatchdogRef.current = null;
                      handleCheckoutError(
                        'Não conseguimos confirmar seu pagamento. Antes de tentar de novo, confirme com a loja se a compra foi registrada.',
                        'E-PAY-TIMEOUT'
                      );
                    }, 60000);

                    break;

                  case 'challenge': {
                    const challengeErr = resolve3DSFailure({
                      action: 'challenge',
                      challengeCanceled,
                      transStatus,
                      reference: cart?.order_number,
                    });
                    await saveThreeDSStatus({
                      cartId: cart.id,
                      tds_server_trans_id,
                      threeds_status: 'C',
                      threeds_message: `[${challengeErr.code}] ${
                        challengeCanceled
                          ? 'Challenge cancelado pelo usuário.'
                          : 'Desafio requerido e não concluído!'
                      }`,
                    });
                    setThreeDSError(challengeErr);
                    setIs3DSLoading(false);
                    setDisabled(false);
                    return;
                  }

                  case 'block': {
                    const blockErr = resolve3DSFailure({
                      action: 'block',
                      transStatus,
                      reference: cart?.order_number,
                    });

                    await saveThreeDSStatus({
                      cartId: cart.id,
                      tds_server_trans_id,
                      threeds_status: transStatus,
                      threeds_message: `[${blockErr.code}] Autenticação negada pelo emissor!`,
                    });

                    await sendThreeDSDebug({
                      token,
                      cartId: cart.id,
                      stage: 'status_eval',
                      error: new Error(
                        `[${blockErr.code}] Emissor negou (trans_status: ${transStatus})`
                      ),
                      context: {
                        ...build3DSContext(),
                        brand: data?.brand,
                        cardNumberRaw: data?.card_number,
                      },
                      threedsData: {
                        tdsServerTransId: tds_server_trans_id,
                        transStatus,
                      },
                      extra: { code: blockErr.code },
                    });
                    setThreeDSError(blockErr);
                    setIs3DSLoading(false);
                    setDisabled(false);
                    return;
                  }

                  default: {
                    const unknownErr = resolve3DSFailure({
                      action: 'unknown',
                      transStatus,
                      reference: cart?.order_number,
                    });
                    await saveThreeDSStatus({
                      cartId: cart.id,
                      tds_server_trans_id,
                      threeds_status: 'UNKNOWN',
                      threeds_message: `[${unknownErr.code}] Status 3DS desconhecido: (${transStatus})`,
                    });
                    setThreeDSError(unknownErr);
                    setIs3DSLoading(false);

                    await sendThreeDSDebug({
                      token,
                      cartId: cart.id,
                      stage: 'status_eval',
                      error: new Error(
                        `Status 3DS desconhecido: ${transStatus}`
                      ),
                      context: {
                        ...build3DSContext(),
                        brand: data?.brand,
                        cardNumberRaw: data?.card_number,
                      },
                      threedsData: {
                        tdsServerTransId: tds_server_trans_id,
                        transStatus,
                        orderData3ds,
                      },
                      extra: { tdsResult },
                    });
                    setDisabled(false);
                    return;
                  }
                }
              }
            } catch (err) {
              console.error('[3DS] Erro no fluxo 3DS:', err?.message || err);
              setIs3DSLoading(false);
              setDisabled(false);

              if (threeDSObserverRef.current) {
                threeDSObserverRef.current.disconnect();
                threeDSObserverRef.current = null;
              }

              const enriched = err && typeof err === 'object' ? err : {};
              if (enriched.tds_server_trans_id && !tds_server_trans_id) {
                tds_server_trans_id = enriched.tds_server_trans_id;
              }
              if (enriched.trans_status && !transStatus) {
                transStatus = enriched.trans_status;
              }

              const isTDSError =
                err?.name === 'TDSError' ||
                (window.TDS?.TDSError && err instanceof window.TDS.TDSError);

              const tdsError = isTDSError
                ? {
                    code: err.errorResponse?.error?.code ?? null,
                    message: err.errorResponse?.error?.message ?? null,
                    details: err.errorResponse?.error?.details ?? null,
                    request_id: err.errorResponse?.error?.request_id ?? null,
                    authentication_id:
                      err.errorResponse?.error?.authentication_id ?? null,
                  }
                : null;

              const httpStatus = isTDSError
                ? err.httpStatus ?? null
                : err?.response?.status ?? null;

              const mapped3DS = resolve3DSFailure({
                error: err,
                httpStatus,
                transStatus,
                reference: cart?.order_number,
              });

              await saveThreeDSStatus({
                cartId: cart.id,
                tds_server_trans_id,
                threeds_status: transStatus || 'ERROR',
                threeds_message: `[${mapped3DS.code}] ${
                  enriched?.message || String(err)
                }`,
              });

              threeDSOpenRef.current = true;
              setThreeDSError(mapped3DS);
              setIs3DSModalOpen(true);

              const isTemporary3DS =
                [502, 503, 504].includes(httpStatus) ||
                /network error/i.test(String(err?.message || ''));

              await sendThreeDSDebug({
                token,
                cartId: cart.id,
                stage: 'authenticate',
                error: err,
                context: {
                  ...build3DSContext(),
                  brand: data?.brand,
                  cardNumberRaw: data?.card_number,
                },
                threedsData: {
                  tdsServerTransId: tds_server_trans_id,
                  transStatus,
                  orderData3ds,
                },
                extra: {
                  httpStatus,
                  isTemporary3DS,
                  code: mapped3DS.code,
                  tdsError,
                  sdk_script_src:
                    document.getElementById('stone-3ds-script')?.src || null,
                  challenge_iframe_seen: threeDSChallengeSeenRef.current,
                  pan_length: normalizePan(data?.card_number).length,
                  pan_luhn: isValidLuhn(data?.card_number),
                  email_normalized:
                    normalizeEmailFor3DS(data?.email) !==
                    String(data?.email || ''),
                  challenge_iframe_present: !!document
                    .getElementById('challengeContainer')
                    ?.querySelector('iframe'),
                  tds_method_filled:
                    (document.getElementById('tdsMethodContainer')?.innerHTML
                      ?.length || 0) > 0,
                },
              });

              return;
            }
          }

          dispatch(
            finalize({
              page_state: {
                setLoading: handleCheckoutLoading,
                setError: handleCheckoutError,
                formatCheckoutError,
              },
              tds_server_trans_id,
            })
          );
        } catch (finalizeError) {
          consoleLogsRef.current = [
            ...consoleLogsRef.current,
            finalizeError.message || 'Erro desconhecido no finalize!',
          ];

          await sendThreeDSDebug({
            token,
            cartId: cart.id,
            stage: 'finalize',
            error: finalizeError,
            context: build3DSContext(),
            threedsData: { tdsServerTransId: tds_server_trans_id, transStatus },
          });

          setIs3DSProcessing(false);
          if (threeDSOpenRef.current) {
            handleCheckoutError(
              'Não conseguimos confirmar seu pagamento. Verifique com a loja antes de tentar novamente.'
            );
          }

          setErrorCheckout(consoleLogsRef.current.join('\n'));
        }
      } catch (err) {
        console.error('Erro no checkout:', err);

        if (err instanceof Yup.ValidationError) {
          const validationErrors = {};
          const firstPath = err?.inner?.[0]?.path || err?.path;

          safeScrollToField(firstPath);

          (err.inner || []).forEach(e => {
            if (e?.path) validationErrors[e.path] = e.message;
          });
          formRef.current?.setErrors(validationErrors);
        }

        const hasAddressError = (err.inner || []).some(e =>
          String(e.path || '').startsWith('address.')
        );

        if (hasAddressError) {
          const currentCep = String(
            formRef.current?.getFieldValue?.('address.cep') || ''
          );
          setSearchedCEP(currentCep);
        }

        if (err.response?.data?.message) {
          toast.error(err.response.data.message, {
            autoClose: err.response.data.duration || 5000,
          });
        }
      } finally {
        setIs3DSLoading(false);
        if (threeDSObserverRef.current) {
          threeDSObserverRef.current.disconnect();
          threeDSObserverRef.current = null;
        }
        setDisabled(false);
      }
    },
    [
      cart,
      clientData,
      dispatch,
      paymentGateway,
      paymentType,
      cartAvailable,
      token,
      validateData,
      telephoneV,
      pixPaymentAvailable,
      saveThreeDSStatus,
      cartAddress,
      build3DSContext,
      handleCheckoutError,
      handleCheckoutLoading,
    ]
  );

  useEffect(() => {
    if (token && id) {
      document.title = 'Meu Carrinho | Viddy';

      const root = document.getElementById('root');
      const body = document.querySelector('body');

      root.classList.add('shared-cart-layout');
      root.style.minHeight = '100vh';

      body.classList.add('shared-cart-layout');

      dispatch(loadCart(id, token));
    } else {
      toast.error('Houve um problema na identificação do carrinho!');
      history.push('/');
    }

    return () => {
      const root = document.getElementById('root');
      const body = document.querySelector('body');

      root.classList.remove('shared-cart-layout');
      root.style.minHeight = 'unset';

      body.classList.remove('shared-cart-layout');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const PAYMENTS_TYPES = useMemo(
    () => [
      {
        id: 1,
        available: cartAvailable,
        label: 'Cartão de Crédito',
        tag: 'Card',
        icon: 'las la-credit-card',
        col_padding: 'px-0',
        value: false,
        checked: (!paymentType && cartAvailable) || paymentType === 'Card',
      },
      {
        id: 2,
        available: pixPaymentAvailable,
        label: 'Pix',
        tag: 'Pix',
        icon: 'las la-university',
        col_padding: 'px-0',
        value: false,
        checked:
          (!paymentType && !cartAvailable && pixPaymentAvailable) ||
          paymentType === 'Pix',
      },
    ],
    [cartAvailable, paymentType, pixPaymentAvailable]
  );

  /**
   * Alternativas de pagamento após recusa/erro: botões que trocam a aba.
   * Usado abaixo do erro inline do cartão e no estado de erro do modal 3DS.
   */
  const getPaymentAlternatives = (onSelect = null) => {
    const current = paymentType === 'Pix' ? 'Pix' : payMode;

    const pick = select => () => {
      setError(null);
      if (onSelect) onSelect();
      select();
    };

    return [
      {
        key: PAY_MODE_GOOGLE_PAY,
        icon: 'gpay',
        label: 'Google Pay',
        hint: 'Sem digitar o cartão',
        available: googlePayAvailable,
        onSelect: pick(() => {
          setPaymentType('Card');
          setPayMode(PAY_MODE_GOOGLE_PAY);
        }),
      },
      {
        key: PAY_MODE_APPLE_PAY,
        icon: 'apay',
        label: 'Apple Pay',
        hint: 'Sem digitar o cartão',
        available: applePayAvailable,
        onSelect: pick(() => {
          setPaymentType('Card');
          setPayMode(PAY_MODE_APPLE_PAY);
        }),
      },
      {
        key: 'Pix',
        icon: 'pix',
        label: 'Pix',
        hint: 'Aprovação na hora',
        available: pixPaymentAvailable,
        onSelect: pick(() => setPaymentType('Pix')),
      },
      {
        key: PAY_MODE_CARD,
        icon: 'card',
        label: 'Cartão de Crédito',
        hint: 'Tente com outro cartão',
        available: cartAvailable,
        onSelect: pick(() => {
          setPaymentType('Card');
          setPayMode(PAY_MODE_CARD);
        }),
      },
    ].filter(alt => alt.available && alt.key !== current);
  };

  const paymentAlternatives = getPaymentAlternatives();

  useEffect(() => {
    if (noNumber) {
      formRef?.current?.setFieldValue('address.number', 'sn');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noNumber]);

  useEffect(() => {
    const isPix =
      PAYMENTS_TYPES.find(type => type.checked).tag === 'Pix' ||
      paymentType === 'Pix';
    if (Date.now() < lastPixFinalizeCallCheckout + 5000) return null;
    if (!cart?.payment_process?.transaction_id && isPix && !cartQrCode) {
      setLoading(true);
      const payments = {
        cielo: hasPixKey ? 105 : 112,
        rede: 105,
        mercadopago: 110,
        pagseguro: 114,
        justa: 118,
        pagarme: 106,
        pagarme_hub: 106,
      };
      dispatch(
        setPaymentData({
          payment_type: payments[paymentGateway],
        })
      );
      dispatch(
        finalize({
          page_state: {
            setLoading,
            setError,
            formatCheckoutError,
          },
        })
      );
      setLastPixFinalizeCallCheckout(Date.now());
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PAYMENTS_TYPES]);

  useEffect(() => {
    const getCities = async () => {
      setCityList(await getCitiesByUf(selectedState));
    };

    getCities();
  }, [selectedState]);

  const getCepValue = () =>
    String(
      formRef.current?.getFieldValue?.('address.cep') || searchedCEP || ''
    );

  const searchCEP = async () => {
    const cep = formRef.current.getFieldValue('address.cep');

    if (searchedCEP === cep) return;
    setSearchedCEP(cep);
    try {
      const { data } = await api.get(`cep/${cep}`);

      const formData = formRef.current.getData();

      formRef.current.setData({
        ...formData,
        address: { ...data },
      });
      const state = stateList.find(s => s.value === data.state);
      if (state) formRef.current.setFieldError('address.state', '');

      setSelectedState(state);
      formRef.current?.setFieldValue('address.state', state);

      const city = {
        label: data.city,
        value: data.city,
      };
      setSelectedCity(city);
      if (city) formRef.current.setFieldError('address.city', '');
      formRef.current?.setFieldValue('address.city', city);
    } catch (err) {
      toast.update(toastId.current, {
        render: 'Endereço não encontrado!',
        type: toast.TYPE.ERROR,
        autoClose: 5000,
        transition: Flip,
      });
    }
  };

  const handleSendToSupport = async () => {
    if (!errorCheckout) return;

    setIsSendingEmail(true);
  };

  const get3DSModalTitle = () => {
    if (threeDSError) return 'Não foi possível concluir';
    if (is3DSProcessing) return 'Processando pagamento';
    if (is3DSSettling) return 'Confirmando autenticação';
    return 'Autenticação de Segurança';
  };

  const render3DSModal = () => (
    <Modal
      isOpen={is3DSModalOpen}
      centered
      backdrop="static"
      keyboard={false}
      toggle={closeThreeDSModal}
      onOpened={() => {
        if (threeDSModalOpenedResolverRef.current) {
          threeDSModalOpenedResolverRef.current();
          threeDSModalOpenedResolverRef.current = null;
        }
      }}
      contentClassName="viddy-3ds-modal"
    >
      <ModalHeader toggle={is3DSProcessing ? undefined : closeThreeDSModal}>
        {get3DSModalTitle()}
      </ModalHeader>
      <ModalBody className="viddy-3ds-body">
        <div id="tdsMethodContainer" style={{ display: 'none' }} />
        <div className="viddy-3ds-frame">
          <div
            id="challengeContainer"
            className={`viddy-3ds-challenge ${
              is3DSLoading ? 'is-loading' : 'is-ready'
            }`}
            style={
              threeDSError || is3DSProcessing || is3DSSettling
                ? { display: 'none' }
                : undefined
            }
          />

          {is3DSLoading && !is3DSSettling && !threeDSError && (
            <div className="viddy-3ds-loading">
              <div className="viddy-3ds-loading-title">
                Carregando autenticação
              </div>
              <div className="viddy-3ds-loading-sub">
                Aguarde alguns segundos. Em alguns casos, o banco pode demorar
                um pouco para responder.
              </div>
              <div className="viddy-3ds-dots">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}

          {is3DSSettling && !is3DSProcessing && !threeDSError && (
            <div className="viddy-3ds-status">
              <div className="viddy-3ds-loading-title">
                Confirmando com o seu banco
              </div>
              <div className="viddy-3ds-loading-sub">
                Isso pode levar alguns instantes. Se você já aprovou no app do
                banco, aguarde nesta tela.
              </div>
              <div className="viddy-3ds-dots">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}

          {is3DSProcessing && !threeDSError && (
            <div className="viddy-3ds-status">
              <div className="viddy-3ds-loading-title">
                Autenticação realizada
              </div>
              <div className="viddy-3ds-loading-sub">
                Estamos concluindo o pagamento. Não feche esta janela.
              </div>
              <div className="viddy-3ds-dots">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}

          {threeDSError && (
            <div className="viddy-3ds-error" role="alert">
              <div className="viddy-3ds-error-title">{threeDSError.title}</div>
              <div className="viddy-3ds-error-message">
                {threeDSError.message}
              </div>
              <div className="viddy-3ds-error-code">
                Código: <strong>{threeDSError.code}</strong>
                {threeDSError.reference
                  ? ` · Pedido #${threeDSError.reference}`
                  : ''}
              </div>
              <div className="viddy-3ds-error-help">
                Se precisar de ajuda, envie este código ao suporte.
              </div>
              <PaymentAlternatives
                compact
                options={getPaymentAlternatives(closeThreeDSModal)}
                themeColor={cart?.store?.customize?.theme_color}
              />
              <Button
                color="white"
                className="px-4 py-2 mt-3 btn-pix-details"
                style={{
                  backgroundColor: cart?.store?.customize?.theme_color,
                  color: isDarkTheme ? 'white' : 'black',
                  border: themeIsWhite && '1px solid #000000',
                }}
                onClick={closeThreeDSModal}
              >
                Entendi
              </Button>
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );

  const showErrorModal = () => {
    if (!errorCheckout) return null;

    return (
      <Modal isOpen={!!errorCheckout} toggle={() => setErrorCheckout(null)}>
        <ModalHeader toggle={() => setErrorCheckout(null)}>
          Erro no Checkout
        </ModalHeader>
        <ModalBody>
          <pre style={{ whiteSpace: 'pre-wrap', color: 'red' }}>
            {errorCheckout}
          </pre>
          <div className="d-flex justify-content-between">
            <Button color="danger" onClick={() => setErrorCheckout(null)}>
              Fechar
            </Button>
            <Button
              color="white"
              onClick={handleSendToSupport}
              disabled={isSendingEmail}
              style={{
                backgroundColor: cart?.store?.customize?.theme_color,
                color: isDarkTheme ? 'white' : 'black',
                border: themeIsWhite && '1px solid #000000',
              }}
            >
              {isSendingEmail ? 'Enviando...' : 'Enviar para Suporte'}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    );
  };

  if (loading)
    return (
      <Loading
        customClassName="shareable-cart-loading"
        customColor={cart?.store?.customize?.theme_color}
        color="custom"
      />
    );

  return (
    <Row className="w-100 mx-0 px-3 pb-2 mb-md-4">
      <Container>
        <Card className="card new-layout my-4 w-100">
          <CardBody className="card-body new-layout p-0 d-flex flex-column-reverse flex-lg-row">
            <Col lg={8} className="m-0 p-0">
              <Row className="w-100 mx-0 mb-4">
                <Nav
                  className="w-100"
                  tabs
                  style={{
                    borderBottomWidth: '3px',
                    borderColor:
                      (themeIsWhite && '#000000') ||
                      cart?.store?.customize?.theme_color ||
                      '#2544b1',
                  }}
                >
                  <Row className="w-100 mx-0 justify-content-center">
                    {PAYMENTS_TYPES.filter(type => type.available).map(type => (
                      <Col
                        lg={type.col_width}
                        className={type.col_padding}
                        key={type.id}
                      >
                        <NavItem>
                          <NavLink
                            className={`d-flex flex-row justify-content-center align-items-center w-100 ${
                              type.checked ? 'active' : ''
                            } font-size-14`}
                            style={{
                              backgroundColor:
                                type.checked &&
                                (cart?.store?.customize?.theme_color ||
                                  '#2544b1'),
                              color: type.checked
                                ? (isDarkTheme && 'white') || 'black'
                                : 'black',
                              border: '1px solid #CCCCCC',
                              borderColor: type.checked
                                ? (themeIsWhite && '#000000') ||
                                  cart?.store?.customize?.theme_color ||
                                  '#2544b1'
                                : '#CCCCCC',
                              transition: 'backgroundColor 0.2s ease-in-out',
                            }}
                            onClick={() => setPaymentType(type.tag)}
                            to="#"
                          >
                            {type.tag === 'Bnpl' && (
                              <span className="px-0 py-0 font-size-20 mr-2">
                                <PixIcon fontSize="inherit" />
                              </span>
                            )}
                            {type.tag === 'Pix' && (
                              <span className="px-0 py-0 font-size-20 mr-2">
                                <PixIcon fontSize="inherit" />
                              </span>
                            )}
                            {type.tag !== 'Pix' && type.tag !== 'Bnpl' && (
                              <i className={`${type.icon} fs-30 mr-2`} />
                            )}

                            <span className="text-nowrap">{type.label}</span>
                          </NavLink>
                        </NavItem>
                      </Col>
                    ))}
                  </Row>
                </Nav>
              </Row>
              {showErrorModal()}
              {render3DSModal()}
              <Form
                onSubmit={handleSubmit}
                ref={formRef}
                data-pagarmecheckout-form
                initialData={
                  paymentProcess
                    ? {
                        name: client?.name,
                        email: client?.email,
                        cpf: client?.cpf,
                        telephone: client?.telephone,
                        ...paymentProcess,
                        installments: {
                          label: paymentProcess?.installments || 1,
                          value: paymentProcess?.installments || 1,
                        },
                      }
                    : {
                        installments: {
                          label: 1,
                          value: 1,
                        },
                      }
                }
                className="py-2 px-4 px-lg-2 pb-5 pt-lg-0"
              >
                {((!paymentType && cartAvailable) ||
                  paymentType === 'Card') && (
                  <>
                    {loadingPage ? (
                      <Loading
                        customClassName="loadingpix"
                        message="Processando sua compra, aguarde!"
                        customColor={cart?.store?.customize?.theme_color}
                        color="custom"
                      />
                    ) : (
                      <>
                        <div
                          style={{
                            maxWidth: '540px',
                            margin: '0 auto',
                          }}
                        >
                          <WalletPicker
                            value={payMode}
                            onChange={setPayMode}
                            googlePayAvailable={googlePayAvailable}
                            applePayAvailable={applePayAvailable}
                            themeColor={cart?.store?.customize?.theme_color}
                            isDarkTheme={isDarkTheme}
                            themeIsWhite={themeIsWhite}
                            disabled={disabled}
                          />
                          <div className="font-size-14 font-weight-600 text-black pb-4">
                            {isWalletMode ? 'Seus dados' : 'Dados do Cartão'}
                          </div>
                          <FormGroup
                            className={
                              isWalletMode
                                ? 'd-none'
                                : 'w-100 d-lg-flex justify-content-center align-items-center'
                            }
                          >
                            <Col xs={12} align="end" className="text-left px-0">
                              <Label className="font-weight-400 font-size-14 mr-3 my-1">
                                Nome impresso*
                              </Label>
                              <Input
                                classRest="link-pix-input"
                                type="text"
                                name="holder"
                                placeholder="Nome"
                                autoCapitalize="characters"
                                onBlur={async e => {
                                  try {
                                    await holderSchema.validate(e.target.value);
                                  } catch (err) {
                                    formRef.current.setFieldError(
                                      'holder',
                                      err.message
                                    );
                                  }
                                }}
                              />
                            </Col>
                          </FormGroup>
                          <Row className="w-100 mx-0 mb-4">
                            <FormGroup
                              className={
                                isWalletMode
                                  ? 'd-none'
                                  : 'mb-4 w-100 d-lg-flex justify-content-center align-items-center'
                              }
                            >
                              <Col
                                xs={12}
                                align="end"
                                className="text-left px-0"
                              >
                                <Label className="font-weight-400 font-size-14 mr-3 my-1">
                                  Número do Cartão*
                                </Label>
                                <Col className="px-0 mx-0">
                                  <InputMask
                                    classes="link-pix-input pr-0"
                                    name="card_number"
                                    placeholder="0000 0000 0000 0000"
                                    mask="9999 9999 9999 9999"
                                    maskChar={null}
                                    type="tel"
                                    onChange={e => {
                                      const flag = CardFlag(e.target.value);
                                      if (flag) {
                                        setBrand(flag);
                                      }
                                      if (e.target.value.length === 19)
                                        document
                                          .querySelector('#expiration_date')
                                          .focus();
                                    }}
                                    onBlur={e => {
                                      if (!CardFlag(e.target.value)) {
                                        formRef.current.setFieldError(
                                          'card_number',
                                          'Não foi possível identificar a bandeira desse cartão'
                                        );
                                      }
                                    }}
                                  />
                                </Col>
                              </Col>
                            </FormGroup>
                            <FormGroup className="w-100 mb-4 d-lg-flex justify-content-center align-items-center">
                              <Row className="mx-0 w-100">
                                <Col
                                  xs={12}
                                  sm={4}
                                  align="end"
                                  className={`text-left pl-0 mb-4 mb-sm-0 ${
                                    isWalletMode ? 'd-none' : ''
                                  }`}
                                >
                                  <Label className="font-weight-400 font-size-14 mr-3 my-1">
                                    Validade*
                                  </Label>
                                  <InputMask
                                    id="expiration_date"
                                    classes="link-pix-input mr-1"
                                    name="expiration_date"
                                    placeholder="MM/AA"
                                    isCheckout
                                    type="tel"
                                    mask="99/99"
                                    formatChars={{
                                      3: '[0-3]',
                                      9: '[0-9]',
                                      4: '[2-3]',
                                    }}
                                    value={expirationDate}
                                    onChange={e => {
                                      setExpirationDate(e.target.value);
                                      if (e.target.value.length === 5)
                                        document
                                          .querySelector('#security_code')
                                          .focus();
                                    }}
                                    onBlur={async e => {
                                      const [month, year] =
                                        e.target.value.split('/');
                                      const yearSufix = String(
                                        new Date().getFullYear()
                                      ).slice(2, 4);

                                      if (Number(month) > 12)
                                        formRef.current.setFieldError(
                                          'expiration_date',
                                          'Mês inválido'
                                        );
                                      if (Number(year) < yearSufix)
                                        formRef.current.setFieldError(
                                          'expiration_date',
                                          'Ano inválido'
                                        );
                                    }}
                                    maskChar={null}
                                  />
                                </Col>
                                <Col
                                  xs={6}
                                  sm={4}
                                  align="end"
                                  className={`text-left px-0 px-sm-2 ${
                                    isWalletMode ? 'd-none' : ''
                                  }`}
                                >
                                  <Label className="font-weight-400 font-size-14 mr-3 my-1">
                                    CVV*
                                  </Label>
                                  <InputMask
                                    classes="link-pix-input"
                                    id="security_code"
                                    name="security_code"
                                    placeholder="CVV"
                                    isCheckout
                                    type="text"
                                    mask="9999"
                                    maskChar={null}
                                  />
                                </Col>
                                <Col
                                  xs={isWalletMode ? 12 : 6}
                                  sm={isWalletMode ? 12 : 4}
                                  className={`text-left padding-adjusts-label pr-0 ${
                                    isWalletMode ? 'pl-0' : ''
                                  }`}
                                >
                                  <Label className="d-block font-weight-400 font-size-14 my-1">
                                    Parcelas*
                                  </Label>
                                  <InputSelect
                                    classRest="link-pix-input"
                                    name="installments"
                                    isCheckout
                                    options={installmentsLimited(
                                      cart?.max_installments || 12
                                    )}
                                    placeholder="Selecione"
                                    defaultValue={{
                                      value: cart?.max_installments,
                                      label: String(cart?.max_installments),
                                    }}
                                    singlePath
                                    customSelect
                                  />
                                </Col>
                              </Row>
                            </FormGroup>
                            <div className="font-size-14 font-weight-600 text-black py-3">
                              Dados de Cobrança
                            </div>
                            <Row className="mx-0 w-100">
                              <Col xs={12} className="text-left px-0">
                                <FormGroup className="w-100 mb-3">
                                  <Label className="font-weight-400 font-size-14 my-1 text-nowrap">
                                    Nome completo*
                                  </Label>
                                  <Input
                                    name="name"
                                    placeholder="Nome"
                                    type="text"
                                    isCheckout
                                    classRest="link-pix-input"
                                  />
                                </FormGroup>
                              </Col>
                              <Col
                                xs={12}
                                sm={6}
                                align="end"
                                className="text-left px-0 pr-sm-2"
                              >
                                <FormGroup className="w-100 mb-4">
                                  <Label className="font-weight-400 font-size-14 mr-3 my-1">
                                    Whatsapp*
                                  </Label>
                                  <InputMask
                                    mask={maskBuilder(telephoneV)}
                                    classes="link-pix-input"
                                    maskChar={null}
                                    value={telephoneV}
                                    name="telephone"
                                    placeholder="Whatsapp"
                                    isCheckout
                                    type="tel"
                                    onChange={e => {
                                      if (
                                        e.target.value.length < 15 ||
                                        e.target.value[5] === '9'
                                      )
                                        setTelephone(e.target.value);
                                    }}
                                  />
                                </FormGroup>
                              </Col>
                              <Col
                                xs={12}
                                sm={6}
                                align="end"
                                className="text-left px-0 pl-sm-2"
                              >
                                <FormGroup className="w-100 mb-4">
                                  <Label className="font-weight-400 font-size-14 mr-3 my-1">
                                    CPF*
                                  </Label>
                                  <InputMask
                                    name="cpf"
                                    classes="link-pix-input"
                                    type="tel"
                                    placeholder="CPF"
                                    mask="999.999.999-99"
                                    isCheckout
                                    maskChar={null}
                                    onBlur={async e => {
                                      try {
                                        await cpfSchema.validate(
                                          e.target.value
                                        );
                                      } catch (err) {
                                        formRef.current.setFieldError(
                                          'cpf',
                                          err.message
                                        );
                                      }
                                    }}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            <Col
                              xs={12}
                              sm={6}
                              align="end"
                              className="text-left px-0 pr-sm-2"
                            >
                              <FormGroup className="w-100 mb-4">
                                <Label className="font-weight-400 font-size-14 mr-3 my-1">
                                  Data de Nascimento*
                                </Label>
                                <InputMask
                                  name="birth_date"
                                  placeholder="DD/MM/AAAA"
                                  mask="99/99/9999"
                                  maskChar={null}
                                  classes="link-pix-input"
                                  onBlur={e =>
                                    validateBirthDate(e.target.value)
                                  }
                                />
                              </FormGroup>
                            </Col>
                            <Col
                              xs={12}
                              sm={6}
                              align="end"
                              className="text-left px-0 pl-sm-2"
                            >
                              <FormGroup className="w-100 mb-4">
                                <Label className="font-weight-400 font-size-14 mr-3 my-1">
                                  E-mail*
                                </Label>
                                <Input
                                  name="email"
                                  placeholder="E-mail"
                                  type="text"
                                  classRest="link-pix-input"
                                  onBlur={async e => {
                                    const v = String(e.target.value || '')
                                      .trim()
                                      .toLowerCase();
                                    formRef.current?.setFieldValue('email', v);

                                    try {
                                      await emailSchema.validate(v);
                                      formRef.current?.setFieldError(
                                        'email',
                                        null
                                      );
                                    } catch (err) {
                                      formRef.current?.setFieldError(
                                        'email',
                                        err.message
                                      );
                                    }
                                  }}
                                />
                              </FormGroup>
                            </Col>
                            <Scope path="address">
                              <Row className="w-100 mx-0">
                                <Row className="w-100 mx-0 mt-2">
                                  <Card className="card border shadow-none mb-4 w-100">
                                    <CardBody className="card-body border px-3 py-3">
                                      <p>
                                        Para compras com cartão é necessário
                                        informar seu endereço de cobrança por
                                        questões de segurança
                                      </p>
                                      <Row className="w-100 mx-0">
                                        <Col className="px-0">
                                          <FormGroup className="w-100 d-lg-flex justify-content-center align-items-center">
                                            <Col
                                              xs={12}
                                              align="end"
                                              className="text-left px-0"
                                            >
                                              <Label className="font-weight-400 font-size-14 mr-3 my-1">
                                                CEP*
                                              </Label>
                                              <InputMask
                                                name="cep"
                                                placeholder="CEP"
                                                mask="99999-999"
                                                isCheckout
                                                type="tel"
                                                classes="link-pix-input"
                                                maskChar={null}
                                                onChange={e =>
                                                  e.target.value.length === 9 &&
                                                  searchCEP()
                                                }
                                              />
                                            </Col>
                                          </FormGroup>
                                          {getCepValue().length === 9 && (
                                            <>
                                              <Row className="w-100 mx-0 mt-lg-2">
                                                <Col
                                                  xs={12}
                                                  lg={6}
                                                  className="px-0 pr-lg-2"
                                                >
                                                  <FormGroup className="w-100 mb-4 d-lg-flex justify-content-center align-items-center">
                                                    <Col
                                                      xs={12}
                                                      align="end"
                                                      className="text-left px-0"
                                                    >
                                                      <Label className="font-weight-400 font-size-14 mr-3 my-1">
                                                        Endereço*
                                                      </Label>
                                                      <Input
                                                        name="street_name"
                                                        isCheckout
                                                        placeholder="Endereço"
                                                        classRest="link-pix-input"
                                                        type="text"
                                                      />
                                                    </Col>
                                                  </FormGroup>
                                                </Col>
                                                <Col
                                                  xs={12}
                                                  lg={6}
                                                  className="px-0 pl-lg-2"
                                                >
                                                  <FormGroup className="w-100 mb-4 d-lg-flex justify-content-center align-items-center">
                                                    <Col
                                                      xs={12}
                                                      align="end"
                                                      className="text-left px-0"
                                                    >
                                                      <Row className="mx-0 d-flex justify-content-between align-items-center">
                                                        <Label className="font-weight-400 font-size-14 mr-3 my-1">
                                                          Número*
                                                        </Label>
                                                        <CustomInput
                                                          type="switch"
                                                          id="noNumberSwitch"
                                                          name="noNumber"
                                                          label="Sem Número"
                                                          checked={noNumber}
                                                          className="text-primary text-center font-weight-400 my-1 mt-lg-0"
                                                          onChange={() =>
                                                            setNoNumber(
                                                              !noNumber
                                                            )
                                                          }
                                                        />
                                                      </Row>
                                                      <Input
                                                        name="number"
                                                        classRest="link-pix-input"
                                                        isCheckout
                                                        placeholder="Nº"
                                                        type="tel"
                                                        disabled={noNumber}
                                                      />
                                                    </Col>
                                                  </FormGroup>
                                                </Col>
                                                <FormGroup className="w-100 mb-4 d-lg-flex justify-content-center align-items-center">
                                                  <Col
                                                    xs={12}
                                                    align="end"
                                                    className="text-left px-0 mt-3 mt-lg-0"
                                                  >
                                                    <Label className="font-weight-400 font-size-14 mr-3 my-1 mt-2 mt-lg-0 ">
                                                      Bairro
                                                    </Label>
                                                    <Input
                                                      name="neighborhood"
                                                      classRest="link-pix-input"
                                                      placeholder="Bairro"
                                                      isCheckout
                                                      type="text"
                                                    />
                                                  </Col>
                                                </FormGroup>
                                              </Row>
                                              <Row className="w-100 mx-0 mt-lg-2">
                                                <FormGroup className="w-100 mb-4 d-lg-flex justify-content-center align-items-center">
                                                  <Col
                                                    xs={12}
                                                    align="end"
                                                    className="text-left px-0"
                                                  >
                                                    <TextAreaWithCounter
                                                      name="complement"
                                                      classRest="link-pix-input"
                                                      classNames={{
                                                        formGroup: 'mb-0',
                                                      }}
                                                      placeholder="Complemento"
                                                      title="Complemento"
                                                      maxSize={50}
                                                      isCheckout
                                                    />
                                                  </Col>
                                                </FormGroup>
                                              </Row>
                                              <Row className="w-100 mx-0 mt-lg-2">
                                                <Col
                                                  xs={12}
                                                  lg={6}
                                                  align="end"
                                                  className="text-left px-0 pr-lg-2"
                                                >
                                                  <FormGroup className="w-100 mb-4">
                                                    <Label className="font-weight-400 font-size-14 mr-3 my-1">
                                                      Estado
                                                    </Label>
                                                    <InputSelect
                                                      name="state"
                                                      className="link-pix-input"
                                                      options={stateList}
                                                      onChange={selectedOption => {
                                                        setSelectedState(
                                                          selectedOption
                                                        );
                                                      }}
                                                      value={selectedState}
                                                      placeholder="Estado"
                                                      isCheckout
                                                    />
                                                  </FormGroup>
                                                </Col>
                                                <Col
                                                  xs={12}
                                                  lg={6}
                                                  align="end"
                                                  className="text-left px-0 pl-lg-2"
                                                >
                                                  <FormGroup className="w-100 mb-4">
                                                    <Label className="font-weight-400 font-size-14 mr-3 my-1">
                                                      Cidade
                                                    </Label>
                                                    <InputSelect
                                                      name="city"
                                                      className="link-pix-input"
                                                      options={cityList}
                                                      onChange={selectedOption => {
                                                        setSelectedCity(
                                                          selectedOption
                                                        );
                                                      }}
                                                      value={selectedCity}
                                                      placeholder="Cidade"
                                                      isDisabled={
                                                        !selectedState
                                                      }
                                                      isCheckout
                                                    />
                                                  </FormGroup>
                                                </Col>
                                              </Row>
                                            </>
                                          )}
                                        </Col>
                                      </Row>
                                    </CardBody>
                                  </Card>
                                </Row>
                              </Row>
                            </Scope>
                          </Row>
                          <Row className="w-100 mx-0 mb-2 justify-content-center">
                            <FormGroup className="d-none">
                              <Input
                                type="text"
                                name="brand"
                                value={brand || ''}
                                readOnly
                              />
                            </FormGroup>
                            {isWalletMode && (
                              <WalletNote walletLabel={walletLabel} />
                            )}
                            {payMode === PAY_MODE_GOOGLE_PAY && (
                              <GooglePayButton
                                disabled={disabled}
                                onClick={() => handleWalletSubmit('GooglePay')}
                              />
                            )}
                            {payMode === PAY_MODE_APPLE_PAY && (
                              <ApplePayButton
                                disabled={disabled}
                                onClick={() => handleWalletSubmit('ApplePay')}
                              />
                            )}
                            {!isWalletMode && (
                              <Button
                                id="submit-button"
                                color="white"
                                className=" px-5 py-3 my-3 btn-pix-details"
                                type="submit"
                                style={{
                                  backgroundColor:
                                    cart?.store?.customize?.theme_color,
                                  color: isDarkTheme ? 'white' : 'black',
                                  border: themeIsWhite && '1px solid #000000',
                                }}
                                disabled={disabled}
                              >
                                <span className="mx-2 font-size-16 font-weight-700 color-white">
                                  Concluir pedido
                                </span>
                              </Button>
                            )}
                          </Row>
                          <Row className="justify-content-center">
                            <span className="text-center">
                              * Campos obrigatórios
                            </span>
                          </Row>
                          {error &&
                            (is3DSModalOpen ||
                              paymentAlternatives.length === 0) && (
                              <Row className="justify-content-center mt-4">
                                <span className="text-center text-danger">
                                  {error}
                                </span>
                              </Row>
                            )}
                          {error &&
                            !is3DSModalOpen &&
                            paymentAlternatives.length > 0 && (
                              <PaymentAlternatives
                                message={error}
                                options={paymentAlternatives}
                                themeColor={cart?.store?.customize?.theme_color}
                              />
                            )}
                        </div>
                      </>
                    )}
                  </>
                )}
                {((!paymentType && !cartAvailable && pixPaymentAvailable) ||
                  paymentType === 'Pix') && (
                  <div>
                    {loadingPage ? (
                      <Loading
                        customClassName="loadingpix"
                        message="Gerando pix, aguarde!"
                        customColor={cart?.store?.customize?.theme_color}
                        color="custom"
                      />
                    ) : (
                      <div
                        style={{
                          maxWidth: '540px',
                          margin: '0 auto',
                        }}
                      >
                        <div className="font-size-14 font-weight-600 text-black pb-4">
                          Realize seu pagamento no Pix!
                        </div>
                        <Row>
                          <Col
                            xs={12}
                            className="d-flex justify-content-center justify-content-lg-start mb-3 mb-lg-0"
                          >
                            <QRCodeLib
                              value={cart?.payment_process?.qrcode || ''}
                              size={200}
                              className="mx-auto mb-4"
                            />
                          </Col>
                          <Col
                            xs={12}
                            className="d-flex flex-column justify-content-start"
                          >
                            <Input
                              name="pix-link"
                              placeholder="Link do Pix"
                              type="text"
                              className="mb-2 mx-2 mx-sm-3"
                              value={cart?.payment_process?.qrcode || ''}
                              disabled
                              multiline
                              style={{
                                height: '5rem',
                                maxHeight: 'none',
                                resize: 'none',
                              }}
                            />
                            <Button
                              id="copyPixData2"
                              tip="Copiar"
                              forceTip
                              tipPlacement="left"
                              color="white"
                              className="py-2 my-3 btn-pix-details"
                              style={{
                                backgroundColor:
                                  cart?.store?.customize?.theme_color,
                                color: isDarkTheme ? 'white' : 'black',
                                border: themeIsWhite && '1px solid #000000',
                              }}
                              onClick={() => {
                                copyStringToClipboard(
                                  cart?.payment_process?.qrcode || '',
                                  'Link Pix copiado com sucesso!'
                                );
                              }}
                            >
                              <span className="ml-2 font-size-16 font-weight-700 color-white">
                                Copiar código PIX
                              </span>
                            </Button>
                          </Col>
                        </Row>
                        <div className="w-100 mx-0 my-4 py-3 d-flex align-items-center">
                          <Col
                            className="px-0 mb-2 mb-lg-0 circle-step"
                            style={{
                              border: `3px solid #2544b1`,
                              borderColor:
                                (themeIsWhite && '#000000') ||
                                cart?.store?.customize?.theme_color ||
                                '#2544b1',
                              color:
                                (themeIsWhite && '#000000') ||
                                cart?.store?.customize?.theme_color ||
                                '#2544b1',
                            }}
                          >
                            1
                          </Col>
                          <Col className="pl-2 font-size-14 font-weight-600 text-gray-600 px-0">
                            Abra o app ou banco de sua preferência. Escolha a
                            opção pagar com Pix "copia e cola", ou código QR.
                          </Col>
                        </div>
                        <div className="w-100 mx-0 my-4 py-3 d-flex align-items-center">
                          <Col
                            className="px-0 mb-2 mb-lg-0 circle-step"
                            style={{
                              border: `3px solid #2544b1`,
                              borderColor:
                                (themeIsWhite && '#000000') ||
                                cart?.store?.customize?.theme_color ||
                                '#2544b1',
                              color:
                                (themeIsWhite && '#000000') ||
                                cart?.store?.customize?.theme_color ||
                                '#2544b1',
                            }}
                          >
                            2
                          </Col>
                          <Col className="pl-2 font-size-14 font-weight-600 text-gray-600 px-0">
                            Copie e cole o código, ou escaneie o código QR com a
                            câmera do seu celular. Confira todas as informações
                            e autorize o pagamento.
                          </Col>
                        </div>
                        <div className="w-100 mx-0 mt-4 pt-3 d-flex align-items-center">
                          <Col
                            className="px-0 mb-2 mb-lg-0 circle-step"
                            style={{
                              border: `3px solid #2544b1`,
                              borderColor:
                                (themeIsWhite && '#000000') ||
                                cart?.store?.customize?.theme_color ||
                                '#2544b1',
                              color:
                                (themeIsWhite && '#000000') ||
                                cart?.store?.customize?.theme_color ||
                                '#2544b1',
                            }}
                          >
                            3
                          </Col>
                          <Col className="pl-2 font-size-14 font-weight-600 text-gray-600 px-0">
                            Você vai receber a confirmação do pagamento por
                            e-mail ou WhatsApp.
                          </Col>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Form>
            </Col>
            <Col className="px-0 col-lg-4 background-order-details">
              <Card className="card new-layout mb-4 w-100 bg-transparent shadow-none">
                <CardBody className="card-body new-layout px-0 px-lg-4 py-0">
                  <Row className="w-100 mx-0 pb-2 mb-md-4 mb-3">
                    <Card className="card new-layout mb-4 w-100">
                      <CardBody className="card-body new-layout px-3 py-3">
                        <div className="font-size-16 font-weight-400 text-black pb-4 d-flex justify-content-between w-100">
                          <span>Resumo do pedido</span>{' '}
                          <span className="font-weight-bold">
                            #{cart?.order_number}
                          </span>
                        </div>
                        <Row className="mx-0">
                          <span className="font-size-14 font-weight-400 text-gray d-block">
                            Valor dos Produtos
                          </span>
                          <span className="font-size-14 font-weight-400 text-black ml-auto">
                            {subscriberId === subscriberViddyComercial
                              ? formatPrice(Number(cart?.amount - viddyTax))
                              : formatPrice(Number(cart?.amount))}
                          </span>
                        </Row>
                        {subscriberId === subscriberViddyComercial && (
                          <Row className="mx-0 mt-2">
                            <span className="font-size-14 font-weight-400 text-gray d-block">
                              Taxa de Serviço
                            </span>
                            <span className="font-size-14 font-weight-400 text-black ml-auto">
                              {formatPrice(Number(viddyTax))}
                            </span>
                          </Row>
                        )}
                        {cart?.is_for_delivery && (
                          <Row className="mx-0 mt-2">
                            <span className="font-size-14 font-weight-400 text-gray d-block">
                              Valor do Frete
                            </span>
                            <span
                              className={`font-size-14 font-weight-400 ml-auto ${
                                cart?.free_freight_applied
                                  ? 'text-green font-weight-bold'
                                  : 'text-black'
                              }`}
                            >
                              {cart?.free_freight_applied
                                ? 'Frete grátis'
                                : formatPrice(cart?.freight_user || 0)}
                            </span>
                          </Row>
                        )}
                        {cart?.giftback?.amount && cart.giftback.amount > 0 && (
                          <Row className="mx-0 mt-2">
                            <span className="font-size-14 font-weight-400 text-gray d-block">
                              Desconto Giftback
                            </span>
                            <span className="font-size-14 font-weight-400 text-red ml-auto">
                              -{formatPrice(cart?.giftback?.amount || 0)}
                            </span>
                          </Row>
                        )}
                        <hr />
                        <Row className="mx-0 mt-2">
                          <span className="font-size-14 font-weight-400 text-gray d-block">
                            Valor Total
                          </span>
                          <span className="font-size-14 font-weight-400 text-black ml-auto">
                            {formatPrice(
                              Number(cart?.amount) +
                                Number(cart?.freight_user || 0) -
                                (cart?.giftback?.amount &&
                                cart.giftback.amount > 0
                                  ? Number(cart.giftback.amount)
                                  : 0)
                            )}
                          </span>
                        </Row>
                      </CardBody>
                    </Card>
                    {cart?.is_for_delivery ? (
                      cart?.client && (
                        <Card className="card new-layout mb-4 w-100">
                          <CardBody className="card-body new-layout px-3 py-3">
                            <p className="font-size-12 font-weight-400 text-gray d-block mb-1">
                              Loja
                            </p>
                            <p className="font-size-14 font-weight-400 text-black">
                              {cart?.store?.fantasy_name}
                            </p>
                            <p className="font-size-12 font-weight-400 text-gray d-block mb-1">
                              Vendedor
                            </p>
                            <p className="font-size-14 font-weight-400 text-black">
                              {cart?.handled_by?.name || cart?.created_by?.name}{' '}
                              -{' '}
                              <a
                                className="font-size-14 font-weight-400 text-black"
                                href={`https://wa.me/${formatWhatsappPhoneNumber(
                                  cart?.handled_by?.telephone ||
                                    cart?.created_by?.telephone
                                )}`}
                              >
                                {cart?.handled_by?.telephone ||
                                  cart?.created_by?.telephone}
                              </a>
                            </p>
                            <hr />
                            <p className="font-size-12 font-weight-400 text-gray d-block mb-1">
                              Cliente
                            </p>
                            <p className="font-size-14 font-weight-400 text-black mb-1">
                              {cart?.client?.name}
                            </p>
                            <p className="font-size-14 font-weight-400 text-black">
                              <a
                                className="font-size-14 font-weight-400 text-black"
                                href={`https://wa.me/${formatWhatsappPhoneNumber(
                                  cart?.client?.telephone
                                )}`}
                              >
                                {cart?.client?.telephone}
                              </a>
                            </p>
                            <hr />
                            {cart?.delivery_address && (
                              <>
                                <p className="font-size-12 font-weight-400 text-gray d-block mb-1">
                                  Endereço de entrega
                                </p>
                                <p className="font-size-14 font-weight-400 text-black">
                                  {cart?.delivery_address?.street_name},{' '}
                                  {cart?.delivery_address?.number} <br />
                                  {cart?.delivery_address?.neighborhood} -{' '}
                                  {cart?.delivery_address?.city} -{' '}
                                  {cart?.delivery_address?.state}
                                </p>
                              </>
                            )}
                          </CardBody>
                        </Card>
                      )
                    ) : (
                      <>
                        <Card className="card new-layout mb-4 w-100">
                          <CardBody className="card-body new-layout px-3 py-3">
                            <span className="font-size-12 font-weight-400 text-gray d-block">
                              Endereço de retirada
                            </span>
                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${cart?.store?.address?.lat},${cart?.store?.address?.long}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <span className="font-size-14 font-weight-400 text-black">
                                {cart?.store?.address?.street_name} ,{' '}
                                {cart?.store?.address?.number} <br />
                                {cart?.store?.address?.neighborhood} -{' '}
                                {cart?.store?.address?.city} -{' '}
                                {cart?.store?.address?.state}
                              </span>
                            </a>
                          </CardBody>
                        </Card>
                        {cart?.client && (
                          <Card className="card new-layout mb-4 w-100">
                            <CardBody className="card-body new-layout px-3 py-3">
                              <span className="font-size-12 font-weight-400 text-gray d-block">
                                Cliente
                              </span>
                              <span className="font-size-14 font-weight-400 text-black">
                                {cart?.client?.name} <br />
                                <a
                                  href={`https://wa.me/${formatWhatsappPhoneNumber(
                                    cart?.client?.telephone
                                  )}`}
                                  className="font-size-14 font-weight-400 text-black d-flex"
                                >
                                  {cart?.client?.telephone}
                                </a>
                              </span>
                            </CardBody>
                          </Card>
                        )}
                      </>
                    )}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </CardBody>
        </Card>
      </Container>
    </Row>
  );
}
